import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { type MonthlyDistribution, past12Months, sampleNormal } from './faker/utils'

export type { MonthlyDistribution }

export type SimulatedMonth = {
  year: number
  month: number
  revenue: number
  expenses: number
  bills: number
  net: number
}

export type SimulatedYear = {
  revenue: number
  expenses: number
  bills: number
  net: number
}

type ImportDataContextValue = {
  layerApiUrl: string
  // Live config — updated by sliders, drives chart/scorecards
  revenue: MonthlyDistribution
  expenses: MonthlyDistribution
  bills: MonthlyDistribution
  setRevenue: (d: Partial<MonthlyDistribution>) => void
  setExpenses: (d: Partial<MonthlyDistribution>) => void
  setBills: (d: Partial<MonthlyDistribution>) => void

  // Committed config — used by import generators, only updated on commitAndRegenerate()
  committedRevenue: MonthlyDistribution
  committedExpenses: MonthlyDistribution
  committedBills: MonthlyDistribution

  // Snapshots live config → committed and re-seeds generators
  commitAndRegenerate: () => void

  simulatedMonths: SimulatedMonth[]
  simulatedYear: SimulatedYear
}

const DEFAULT_REVENUE: MonthlyDistribution = { mean: 10000, stdDev: 2000, min: 5000, max: 20000 }
const DEFAULT_EXPENSES: MonthlyDistribution = { mean: 6000, stdDev: 1000, min: 3000, max: 10000 }
const DEFAULT_BILLS: MonthlyDistribution = { mean: 1250, stdDev: 200, min: 500, max: 3000 }

const ImportDataContext = createContext<ImportDataContextValue | null>(null)

export const ImportDataProvider = ({
  children,
  layerApiUrl,
}: {
  children: ReactNode
  layerApiUrl: string
}) => {
  // Live (slider) state
  const [revenue, setRevenueState] = useState<MonthlyDistribution>(DEFAULT_REVENUE)
  const [expenses, setExpensesState] = useState<MonthlyDistribution>(DEFAULT_EXPENSES)
  const [bills, setBillsState] = useState<MonthlyDistribution>(DEFAULT_BILLS)

  // Committed (generator) state — starts equal to defaults
  const [committedRevenue, setCommittedRevenue] = useState<MonthlyDistribution>(DEFAULT_REVENUE)
  const [committedExpenses, setCommittedExpenses] = useState<MonthlyDistribution>(DEFAULT_EXPENSES)
  const [committedBills, setCommittedBills] = useState<MonthlyDistribution>(DEFAULT_BILLS)

  // Seed incremented by commitAndRegenerate to force generator re-run
  const [seed, setSeed] = useState(0)

  const setRevenue = useCallback((d: Partial<MonthlyDistribution>) => {
    setRevenueState(prev => ({ ...prev, ...d }))
  }, [])
  const setExpenses = useCallback((d: Partial<MonthlyDistribution>) => {
    setExpensesState(prev => ({ ...prev, ...d }))
  }, [])
  const setBills = useCallback((d: Partial<MonthlyDistribution>) => {
    setBillsState(prev => ({ ...prev, ...d }))
  }, [])

  const commitAndRegenerate = useCallback(() => {
    setCommittedRevenue({ ...revenue })
    setCommittedExpenses({ ...expenses })
    setCommittedBills({ ...bills })
    setSeed(s => s + 1)
  }, [revenue, expenses, bills])

  // Chart preview uses live config + seed
  const simulatedMonths = useMemo<SimulatedMonth[]>(() => {
    return past12Months().map(([year, month]) => {
      const r = sampleNormal(revenue.mean, revenue.stdDev, revenue.min, revenue.max)
      const e = sampleNormal(expenses.mean, expenses.stdDev, expenses.min, expenses.max)
      const b = sampleNormal(bills.mean, bills.stdDev, bills.min, bills.max)
      return { year, month, revenue: r, expenses: e, bills: b, net: r - e - b }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenue, expenses, bills, seed])

  const simulatedYear = useMemo<SimulatedYear>(
    () =>
      simulatedMonths.reduce(
        (acc, m) => ({
          revenue: acc.revenue + m.revenue,
          expenses: acc.expenses + m.expenses,
          bills: acc.bills + m.bills,
          net: acc.net + m.net,
        }),
        { revenue: 0, expenses: 0, bills: 0, net: 0 },
      ),
    [simulatedMonths],
  )

  return (
    <ImportDataContext.Provider
      value={{
        layerApiUrl,
        revenue,
        expenses,
        bills,
        setRevenue,
        setExpenses,
        setBills,
        committedRevenue,
        committedExpenses,
        committedBills,
        commitAndRegenerate,
        simulatedMonths,
        simulatedYear,
      }}
    >
      {children}
    </ImportDataContext.Provider>
  )
}

export const useImportData = (): ImportDataContextValue => {
  const ctx = useContext(ImportDataContext)
  if (!ctx) throw new Error('useImportData must be used within ImportDataProvider')
  return ctx
}

export const useLayerApiUrl = (): string => useImportData().layerApiUrl
